import React, { useRef, useState } from "react";
import toast from "react-hot-toast";
import { useFirestoreAdmin } from "../context/FirestoreAdminProvider";
import { parseImportFile, exportCsv, exportExcel, exportJson, detectDuplicates } from "../services/importExportService";
import { doc, writeBatch } from "firebase/firestore";
import { runtimeDb as db } from "../../firestoreStudio/runtime/runtimeDb";
import { BATCH_SIZE } from "../constants";
import { trackWrite } from "../services/monitorService";
import { registerCollection, touchCollection } from "../../../services/collectionRegistryService";

export default function ImportExportPage() {
  const { collectionName, rows, selectCollection, allCollections, loadDocuments, user } = useFirestoreAdmin();
  const fileRef = useRef(null);
  const [preview, setPreview] = useState([]);
  const [dupes, setDupes] = useState([]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const parsed = await parseImportFile(file);
      setPreview(parsed.slice(0, 50));
      const existing = new Set(rows.map((r) => r.id));
      setDupes(detectDuplicates(parsed, existing));
      toast.success(`Parsed ${parsed.length} rows — previewing first 50`);
    } catch (err) {
      toast.error(err?.message || "Import parse failed");
    }
  };

  const importRows = async () => {
    if (!preview.length) return;
    if (window.prompt("Type IMPORT to write preview rows") !== "IMPORT") return;
    let batch = writeBatch(db);
    let count = 0;
    for (const row of preview) {
      const id = row.id || row.docId;
      if (!id) continue;
      const { id: _id, docId, ...data } = row;
      batch.set(doc(db, collectionName, id), data);
      count += 1;
      if (count % BATCH_SIZE === 0) {
        await batch.commit();
        batch = writeBatch(db);
      }
    }
    if (count % BATCH_SIZE !== 0) await batch.commit();
    const actorId = user?.uid || user?.email || null;
    await registerCollection(collectionName, { createdBy: actorId });
    await touchCollection(collectionName, actorId);
    trackWrite(count);
    toast.success(`Imported ${count} documents`);
    await loadDocuments();
  };

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-2xl font-bold">Import / Export</h1>
        <p className="text-sm text-slate-600">CSV, Excel, JSON with preview and duplicate detection.</p>
      </header>
      <div className="fa-card flex flex-wrap gap-2 p-4">
        <select className="rounded-lg border px-2 py-1.5 text-sm" value={collectionName} onChange={(e) => selectCollection(e.target.value)}>
          {allCollections.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <button type="button" className="fa-btn" onClick={() => exportJson(rows, `${collectionName}.json`)}>Export JSON</button>
        <button type="button" className="fa-btn" onClick={() => exportCsv(rows, `${collectionName}.csv`)}>Export CSV</button>
        <button type="button" className="fa-btn" onClick={() => exportExcel(rows, `${collectionName}.xlsx`)}>Export Excel</button>
        <input ref={fileRef} type="file" accept=".csv,.json,.xlsx,.xls" className="hidden" onChange={onFile} />
        <button type="button" className="fa-btn fa-btn-primary" onClick={() => fileRef.current?.click()}>Import file</button>
        <button type="button" className="fa-btn fa-btn-primary" onClick={importRows} disabled={!preview.length}>Execute import</button>
      </div>
      {dupes.length > 0 && (
        <div className="rounded-lg bg-amber-50 p-3 text-sm text-amber-900">
          {dupes.length} potential duplicate(s) detected in file.
        </div>
      )}
      <div className="fa-card fa-table-wrap">
        <table className="fa-table">
          <thead><tr><th>Preview rows</th></tr></thead>
          <tbody>
            {preview.map((r, i) => (
              <tr key={i}><td className="font-mono text-xs">{JSON.stringify(r).slice(0, 200)}</td></tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
